// L'EFFACEUR — Pack Audit Pouvoir d'Achat
// YY.2 : Refonte UX complète
//   - Sidebar contextuelle (audit) à gauche comme Déménagement/Héritage
//   - Persistance de l'étape dans localStorage (F5 ne reset plus)
//   - Bouton "Retour au dashboard" sur l'écran final
//   - Hero cyber/circuit cohérent avec la DA du site
//   - 3 cartes impact redesignées avec icônes SVG + glow
//   - Plus de cachet visuel global (grid, scan-line, glow)
//
// Tunnel : Upload (CSV/PDF) → Loading → Résultats avec sélection → Lancer mission

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload, FileText, TrendingDown, Check, AlertCircle, Loader2, ArrowRight,
  Shield, Zap, Lock, ArrowLeft, ScanSearch, Brain, Target
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { ContextualSidebar, MobileMenu } from '../components/layout/ContextualSidebar';

const CATEGORY_LABELS = {
  streaming: { label: 'Streaming', emoji: '📺' },
  telecom: { label: 'Télécom', emoji: '📱' },
  energie: { label: 'Énergie', emoji: '⚡' },
  assurance: { label: 'Assurance', emoji: '🛡️' },
  banque: { label: 'Banque', emoji: '🏦' },
  sport: { label: 'Sport / Fitness', emoji: '💪' },
  presse: { label: 'Presse / Logiciel', emoji: '📰' },
  jeux: { label: 'Jeux vidéo', emoji: '🎮' },
  autre: { label: 'Autres', emoji: '📦' },
};

// Persistance F5
const LS_KEY = 'audit_pack_state_v1';

const loadPersistedState = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Si l'audit a plus de 24h, on jette
    if (parsed?.timestamp && Date.now() - parsed.timestamp > 24 * 3600 * 1000) {
      localStorage.removeItem(LS_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const persistState = (state) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ ...state, timestamp: Date.now() }));
  } catch {
    /* localStorage plein, on ignore */
  }
};

const clearPersistedState = () => {
  try { localStorage.removeItem(LS_KEY); } catch {}
};

// Étapes pour la sidebar (correspond à MISSION_STEPS.audit dans ContextualSidebar)
const STEP_TO_INDEX = { upload: 1, loading: 2, results: 3 };

const AuditPackPage = () => {
  const navigate = useNavigate();

  // --- État, restauré depuis localStorage si dispo
  const persisted = loadPersistedState();
  const [step, setStep] = useState(persisted?.step || 'upload');
  const [file, setFile] = useState(null);
  const [audit, setAudit] = useState(persisted?.audit || null);
  const [selectedKeys, setSelectedKeys] = useState(new Set(persisted?.selectedKeys || []));
  const [launching, setLaunching] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Si on a un audit_id persisté mais pas de détail, on recharge
  useEffect(() => {
    const p = loadPersistedState();
    if (p?.auditId && !p?.audit) {
      const token = localStorage.getItem('token');
      if (!token) return;
      axios.get(`${API}/audit-pack/${p.auditId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        setAudit(res.data);
        setStep('results');
      }).catch(() => {
        clearPersistedState();
        setStep('upload');
      });
    }
  }, []);

  // Persistance à chaque changement
  useEffect(() => {
    if (step === 'results' && audit) {
      persistState({
        step,
        auditId: audit.audit_id,
        audit,
        selectedKeys: Array.from(selectedKeys),
      });
    } else if (step === 'upload') {
      // Sur retour à upload, on nettoie
      // Mais pas avant un éventuel rechargement initial → on attend que step soit explicitement upload après mount
    }
  }, [step, audit, selectedKeys]);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setStep('loading');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/audit-pack/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const detail = await axios.get(`${API}/audit-pack/${res.data.audit_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAudit(detail.data);
      setStep('results');
      toast.success(`${detail.data.recurring_count} prélèvements récurrents détectés`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur upload';
      toast.error(msg);
      setStep('upload');
    }
  }, [file]);

  const toggleSelect = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllSavings = () => {
    if (!audit) return;
    // CCC.16 : le backend stocke maintenant `recurring_subscriptions` (orchestrator).
    // On garde un fallback `recurring` au cas où des anciens audits existent en DB.
    const withSavings = (audit.recurring_subscriptions || audit.recurring || [])
      .filter(r => (r.savings?.vs_cheapest_annual || 0) > 0)
      .map(r => r.key);
    setSelectedKeys(new Set(withSavings));
  };

  const handleLaunch = async () => {
    if (selectedKeys.size === 0) {
      toast.error('Sélectionne au moins un abonnement à résilier');
      return;
    }
    setLaunching(true);
    try {
      const token = localStorage.getItem('token');
      // Sauvegarde la sélection
      await axios.post(`${API}/audit-pack/${audit.audit_id}/select`, {
        keys: Array.from(selectedKeys),
      }, { headers: { Authorization: `Bearer ${token}` } });

      // CCC.12 / DDD.3 : tente de lancer. Si 402 Payment Required → on redirige vers Stripe.
      // Après création mission, on bascule sur le tunnel /missions/audit pour signature du mandat
      // (continuité A→Z : sélection → paiement → mandat → confirmation).
      try {
        const res = await axios.post(`${API}/audit-pack/${audit.audit_id}/launch`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Mission créée : ${res.data.estimated_annual_savings}€/an d'économies estimées`);
        clearPersistedState();
        navigate(`/missions/audit?mission=${res.data.mission_id}&new=true`);
        return;
      } catch (launchErr) {
        if (launchErr.response?.status !== 402) throw launchErr;
        // 402 → besoin de payer 29€ pour débloquer
        const checkoutRes = await axios.post(
          `${API}/audit-pack/${audit.audit_id}/checkout-unlock`,
          { origin_url: window.location.origin },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (checkoutRes.data?.status === 'unlocked_via_bundle') {
          // Bundle Sérénité : pas de paiement, on relance direct
          const res = await axios.post(`${API}/audit-pack/${audit.audit_id}/launch`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success(`Inclus dans votre Pack Sérénité — mission créée`);
          clearPersistedState();
          navigate(`/missions/audit?mission=${res.data.mission_id}&new=true`);
          return;
        }
        if (checkoutRes.data?.checkout_url) {
          // Persiste l'audit avant redirection Stripe (au retour, on reprendra)
          window.location.href = checkoutRes.data.checkout_url;
          return;
        }
        toast.error('Impossible de créer le paiement');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lancement mission');
    } finally {
      setLaunching(false);
    }
  };

  // CCC.12 / DDD.3 : au retour de Stripe success, on confirme le paiement,
  // on AUTO-LANCE la mission, puis on redirige vers le tunnel de signature mandat.
  // Avant DDD.3 le user devait re-cliquer "Lancer la résiliation" → confusion totale.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const unlock = params.get('unlock');
    const auditIdParam = params.get('audit_id');
    if (unlock === 'success' && auditIdParam) {
      const token = localStorage.getItem('token');
      (async () => {
        try {
          const auditRes = await axios.get(`${API}/audit-pack/${auditIdParam}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const sessionId = auditRes.data?.unlock_session_id;

          // 1. Confirme l'unlock côté backend (idempotent — supporte si déjà payé)
          if (sessionId) {
            try {
              await axios.get(
                `${API}/audit-pack/${auditIdParam}/confirm-unlock?session_id=${sessionId}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (_) { /* on essaie quand même le launch */ }
          }

          // 2. AUTO-LAUNCH la mission de résiliation
          toast.success('Paiement confirmé. Lancement des résiliations...');
          try {
            const launchRes = await axios.post(
              `${API}/audit-pack/${auditIdParam}/launch`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            // 3. Redirige vers le tunnel /missions/audit pour signature du mandat
            const newMissionId = launchRes.data?.mission_id;
            clearPersistedState();
            if (newMissionId) {
              navigate(`/missions/audit?mission=${newMissionId}&new=true`);
            } else {
              navigate(`/dashboard?mission=${auditIdParam}`);
            }
          } catch (launchErr) {
            // Si le webhook Stripe n'est pas encore arrivé, l'audit n'est pas unlock_paid
            // → on remet le user sur l'écran results avec un toast pour relancer manuellement.
            const detail = launchErr.response?.data?.detail || '';
            if (launchErr.response?.status === 402) {
              toast.warning('Paiement en cours de confirmation. Recharge la page dans 10 sec puis clique "Lancer".');
              setAudit({ ...auditRes.data, unlock_paid: true });
              setStep('results');
            } else {
              toast.error(detail || 'Erreur lors du lancement automatique');
              setAudit(auditRes.data);
              setStep('results');
            }
          }
        } catch (e) {
          toast.error('Impossible de récupérer le statut du paiement');
        }
      })();
    } else if (unlock === 'cancelled') {
      toast.info('Paiement annulé. Tu peux relancer quand tu veux.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetAndReturn = () => {
    clearPersistedState();
    setStep('upload');
    setAudit(null);
    setSelectedKeys(new Set());
    setFile(null);
  };

  const handleBackToDashboard = () => {
    clearPersistedState();
    navigate('/dashboard');
  };

  // Données utilisateur minimales pour la sidebar
  const userEmail = localStorage.getItem('userEmail') || '';
  const isAdmin = localStorage.getItem('isAdmin') === '1';
  const sidebarUser = { email: userEmail, is_admin: isAdmin };
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userEmail');
    clearPersistedState();
    navigate('/login');
  };

  const currentStepIndex = STEP_TO_INDEX[step] || 1;
  const completedSteps = step === 'results' ? ['upload', 'analyse'] : step === 'loading' ? ['upload'] : [];

  // Layout : Sidebar à gauche + contenu principal
  return (
    <div className="min-h-screen bg-void text-white flex">
      {/* Sidebar contextuelle (mode mission audit) */}
      <ContextualSidebar
        context="mission"
        missionType="audit"
        currentStep={currentStepIndex}
        completedSteps={completedSteps}
        user={sidebarUser}
        onLogout={handleLogout}
      />

      {/* Mobile menu burger */}
      <MobileMenu
        context="mission"
        missionType="audit"
        currentStep={currentStepIndex}
        completedSteps={completedSteps}
        user={sidebarUser}
        onLogout={handleLogout}
        isOpen={mobileMenuOpen}
        setIsOpen={setMobileMenuOpen}
      />

      {/* Contenu principal */}
      <main className="flex-1 min-w-0 relative">
        {/* Grid de fond cyber */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(0,245,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,245,255,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* Glow ambient en haut */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-cyan-500/10 blur-[120px] pointer-events-none" />

        <div className="relative z-10">
          {step === 'upload' && (
            <UploadStep file={file} setFile={setFile} onUpload={handleUpload} />
          )}
          {step === 'loading' && <LoadingStep />}
          {step === 'results' && audit && (
            <ResultsStep
              audit={audit}
              selectedKeys={selectedKeys}
              toggleSelect={toggleSelect}
              selectAllSavings={selectAllSavings}
              clearSelection={() => setSelectedKeys(new Set())}
              onLaunch={handleLaunch}
              launching={launching}
              onBackToDashboard={handleBackToDashboard}
              onResetAndReturn={handleResetAndReturn}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ÉTAPE 1 : Upload — Hero cyber/circuit + 3 cartes impact redesignées
// ═══════════════════════════════════════════════════════════════════════════
const UploadStep = ({ file, setFile, onUpload }) => {
  return (
    <div className="p-6 md:p-10 pb-20">
      <div className="max-w-4xl mx-auto py-8 md:py-12">
        {/* HERO — pas de banderole "GRATUIT" ostentatoire en haut */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          {/* Pastille discrète */}
          <div className="inline-flex items-center gap-2 bg-cyan-500/5 border border-cyan-500/20 rounded-full px-3 py-1 mb-6">
            <ScanSearch className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[11px] font-mono text-cyan-400/80 uppercase tracking-[0.2em]">
              // AUDIT_POUVOIR_DACHAT
            </span>
          </div>

          {/* Titre principal */}
          <h1 className="font-orbitron text-4xl md:text-6xl font-black mb-6 leading-tight">
            <span className="text-white">Combien tu </span>
            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">
              paies en trop
            </span>
            <br />
            <span className="text-white">chaque mois ?</span>
          </h1>

          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Upload ton relevé bancaire. On scanne tes prélèvements récurrents,
            on identifie les abonnements payés trop chers, on te liste les économies réelles.
          </p>

          {/* Promesse forte sous le sous-titre */}
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-mono">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
            <span className="text-success font-bold">Audit gratuit</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-300">Tu ne paies que si on te fait économiser</span>
          </div>
        </motion.div>

        {/* ZONE UPLOAD — relookée avec scan-line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative"
        >
          {/* Glow autour de la zone upload */}
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-magenta-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-50" />

          <div className="relative glass-card border-2 border-dashed border-cyan-500/40 hover:border-cyan-400/60 transition-colors p-10 md:p-14 text-center rounded-xl overflow-hidden">
            {/* Scan-line décoratif */}
            <div
              className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60"
              style={{ animation: 'scan-line 3s linear infinite' }}
            />

            <input
              type="file"
              id="csv-upload"
              accept=".csv,.tsv,.txt,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden"
            />
            <label htmlFor="csv-upload" className="cursor-pointer block">
              {!file ? (
                <>
                  <div className="relative inline-block mb-6">
                    <div className="absolute inset-0 bg-cyan-400/30 blur-2xl rounded-full" />
                    <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-magenta-500/20 border border-cyan-400/40 flex items-center justify-center mx-auto">
                      <Upload className="w-10 h-10 text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-white mb-3">
                    Dépose ton relevé bancaire
                  </div>
                  <div className="text-sm text-gray-400 max-w-md mx-auto">
                    BNP, Crédit Agricole, SG, Boursorama, Revolut, N26, LCL, Hello Bank,
                    Caisse d'Épargne, Banque Populaire — <span className="text-cyan-400">CSV ou PDF</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-4 font-mono">
                    // TÉLÉCHARGE_DEPUIS_TON_ESPACE_CLIENT · MAX_5MO
                  </div>
                </>
              ) : (
                <>
                  <div className="relative inline-block mb-4">
                    <div className="absolute inset-0 bg-success/30 blur-2xl rounded-full" />
                    <div className="relative w-16 h-16 rounded-2xl bg-success/10 border border-success/40 flex items-center justify-center mx-auto">
                      <FileText className="w-8 h-8 text-success" />
                    </div>
                  </div>
                  <div className="text-lg font-bold text-white mb-1">{file.name}</div>
                  <div className="text-sm text-gray-400 font-mono">
                    {(file.size / 1024).toFixed(1)} Ko · prêt à analyser
                  </div>
                </>
              )}
            </label>
          </div>
        </motion.div>

        {/* CTA */}
        {file && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-8 flex items-center justify-center gap-3 flex-wrap"
          >
            <Button onClick={() => setFile(null)} variant="outline">
              Changer de fichier
            </Button>
            <Button
              onClick={onUpload}
              className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold px-8 py-6 text-base shadow-[0_0_30px_rgba(0,245,255,0.4)] hover:shadow-[0_0_40px_rgba(0,245,255,0.6)] transition-all"
            >
              Lancer l'audit <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* 3 CARTES IMPACT — redesignées avec icônes SVG, glow, hover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          <ImpactCard
            icon={Shield}
            iconColor="text-cyan-400"
            glowColor="bg-cyan-500/20"
            badge="100% RGPD"
            title="Données protégées"
            desc="Traitement en France, suppression auto après 30 jours. Aucune revente, aucune fuite."
          />
          <ImpactCard
            icon={Zap}
            iconColor="text-success"
            glowColor="bg-success/20"
            badge="GRATUIT"
            title="Aucun coût caché"
            desc="L'audit est offert. Tu paies uniquement 20% des économies réelles obtenues."
          />
          <ImpactCard
            icon={Lock}
            iconColor="text-magenta-400"
            glowColor="bg-magenta-500/20"
            badge="ZÉRO PRESSION"
            title="Tu choisis"
            desc="Sélectionne uniquement les abonnements que tu veux résilier. Aucun engagement."
          />
        </motion.div>

        {/* Mini-process en bas, façon "comment ça marche" */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-12 bg-gray-800" />
            <span className="text-xs font-mono text-gray-500 uppercase tracking-[0.3em]">
              Comment ça marche
            </span>
            <div className="h-px w-12 bg-gray-800" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ProcessStep n="01" icon={Upload} title="Upload" desc="Ton relevé bancaire CSV ou PDF" />
            <ProcessStep n="02" icon={Brain} title="Analyse IA" desc="Détection des récurrents + comparaison marché" />
            <ProcessStep n="03" icon={Target} title="Plan d'action" desc="Tu choisis, on résilie pour toi" />
          </div>
        </motion.div>
      </div>

      {/* Animation CSS scan-line */}
      <style>{`
        @keyframes scan-line {
          0% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(60px); opacity: 1; }
          100% { transform: translateY(0); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

const ImpactCard = ({ icon: Icon, iconColor, glowColor, badge, title, desc }) => (
  <div className="group relative">
    {/* Glow au hover */}
    <div className={`absolute -inset-0.5 ${glowColor} blur-xl opacity-0 group-hover:opacity-100 transition-opacity rounded-xl`} />

    <div className="relative glass-card border border-gray-800 group-hover:border-cyan-400/40 rounded-xl p-6 transition-all h-full">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-lg bg-gray-900/80 border border-gray-800 flex items-center justify-center ${iconColor}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded ${iconColor} bg-gray-900/80 border border-gray-800`}>
          {badge}
        </span>
      </div>
      <div className="font-orbitron text-base font-bold text-white mb-1">{title}</div>
      <div className="text-sm text-gray-400 leading-relaxed">{desc}</div>
    </div>
  </div>
);

const ProcessStep = ({ n, icon: Icon, title, desc }) => (
  <div className="relative bg-gray-900/40 border border-gray-800 rounded-lg p-5 hover:border-cyan-500/30 transition-colors">
    <div className="absolute -top-3 -left-3 w-10 h-10 rounded-lg bg-void border border-cyan-500/30 flex items-center justify-center">
      <span className="font-orbitron text-xs font-black text-cyan-400">{n}</span>
    </div>
    <div className="ml-8">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-cyan-400" />
        <div className="font-bold text-white text-sm">{title}</div>
      </div>
      <div className="text-xs text-gray-500">{desc}</div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// ÉTAPE 2 : Loading — animation cyber
// ═══════════════════════════════════════════════════════════════════════════
const LoadingStep = () => {
  const lines = [
    'PARSE_RELEVÉ_BANCAIRE...',
    'DÉTECTION_RÉCURRENTS...',
    'CATALOG_MATCH (Spotify, EDF, Netflix...)',
    'COMPARAISON_PRIX_MARCHÉ...',
    'CALCUL_ÉCONOMIES_POTENTIELLES...',
  ];
  return (
    <div className="flex items-center justify-center min-h-screen p-6">
      <div className="text-center max-w-md">
        <div className="relative inline-block mb-8">
          <div className="absolute inset-0 bg-cyan-500/30 blur-3xl rounded-full" />
          <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-magenta-500/20 border border-cyan-400/40 flex items-center justify-center mx-auto">
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin" />
          </div>
        </div>
        <div className="font-orbitron text-2xl md:text-3xl font-black text-white mb-3">
          Analyse en cours
        </div>
        <div className="text-gray-400 mb-8">
          On parse, on détecte, on compare aux prix marché.
        </div>

        <div className="text-left space-y-2 font-mono text-xs bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          {lines.map((l, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.4 }}
              className="flex items-center gap-2 text-cyan-400/80"
            >
              <span className="text-gray-600">$</span>
              <span>{l}</span>
              {i === lines.length - 1 && <span className="animate-pulse">_</span>}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ÉTAPE 3 : Résultats — avec bouton retour dashboard
// ═══════════════════════════════════════════════════════════════════════════
const ResultsStep = ({
  audit, selectedKeys, toggleSelect, selectAllSavings, clearSelection,
  onLaunch, launching, onBackToDashboard, onResetAndReturn,
}) => {
  // CCC.16 : lit `recurring_subscriptions` (nouveau format orchestrator) avec fallback `recurring`
  const recurring = audit.recurring_subscriptions || audit.recurring || [];
  const byCategory = audit.by_category || {};
  const selectedSavingsAnnual = recurring
    .filter(r => selectedKeys.has(r.key))
    .reduce((sum, r) => sum + (r.savings?.vs_cheapest_annual || r.annual_cost || 0), 0);

  return (
    <div className="p-6 md:p-10">
      <div className="max-w-6xl mx-auto py-6 md:py-8">
        {/* TOP BAR — bouton retour dashboard + nouvel audit */}
        {/* DDD.2 : "Nouvel audit" rendu visible (CTA cyan) — sinon le user reste sur l'ancien bulletin
            sans comprendre comment relancer un scan (persistance localStorage). */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors text-sm font-mono"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au dashboard
          </button>
          <button
            onClick={() => {
              if (window.confirm('Lancer un nouveau scan ? Le bulletin actuel sera remplacé par le nouveau scan.')) {
                onResetAndReturn();
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-500/50 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 hover:text-cyan-200 transition-colors text-sm font-mono font-bold"
          >
            <ScanSearch className="w-4 h-4" />
            Refaire un scan
          </button>
        </div>

        {/* Header synthèse */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-success/10 border border-success/30 rounded-full px-3 py-1 mb-4">
            <Check className="w-3.5 h-3.5 text-success" />
            <span className="text-[11px] font-mono text-success uppercase tracking-[0.2em]">
              // AUDIT_TERMINÉ
            </span>
          </div>
          <h1 className="font-orbitron text-3xl md:text-4xl font-black mb-3 leading-tight">
            <span className="text-cyan-400">Résultats prêts.</span>
            {audit.total_savings_potential_annual > 0 ? (
              <>
                <br className="md:hidden" />
                <span className="text-white"> Tu peux économiser </span>
                <span className="text-success">{audit.total_savings_potential_annual.toFixed(0)}€/an</span>
                <span className="text-white">.</span>
              </>
            ) : (
              <span className="text-white"> On a tout passé au crible.</span>
            )}
          </h1>
          <p className="text-gray-400 text-sm md:text-base">
            <span className="text-cyan-400 font-bold">{audit.recurring_count}</span> prélèvements récurrents
            détectés sur <span className="text-white">{audit.transactions_count}</span> transactions
            <span className="text-gray-600"> · </span>
            <span className="font-mono text-xs">{audit.period_start} → {audit.period_end}</span>
          </p>
        </motion.div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Mensuel" value={`${audit.total_monthly_subscriptions?.toFixed(0) || 0}€`} />
          <StatCard label="Annuel" value={`${audit.total_annual_subscriptions?.toFixed(0) || 0}€`} />
          <StatCard
            label="Éco. potentielle"
            value={`${audit.total_savings_potential_annual?.toFixed(0) || 0}€/an`}
            color="success"
          />
          <StatCard
            label="Sélectionnés"
            value={selectedKeys.size}
            sub={`${selectedSavingsAnnual.toFixed(0)}€/an`}
            color="cyan"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <Button
            onClick={selectAllSavings}
            variant="outline"
            className="border-success/30 text-success hover:bg-success/10"
          >
            Tout sélectionner ({recurring.filter(r => (r.savings?.vs_cheapest_annual || 0) > 0).length} avec économies)
          </Button>
          <Button onClick={clearSelection} variant="outline">
            Tout désélectionner
          </Button>
          <div className="flex-1" />
          <Button
            onClick={onLaunch}
            disabled={selectedKeys.size === 0 || launching}
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold shadow-[0_0_20px_rgba(0,245,255,0.3)]"
          >
            {launching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Lancer la résiliation ({selectedKeys.size}) <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Liste par catégorie */}
        <div className="space-y-6">
          {Object.entries(byCategory).map(([catKey, cat]) => {
            const catInfo = CATEGORY_LABELS[catKey] || CATEGORY_LABELS.autre;
            const items = recurring.filter(r => r.category === catKey);
            if (items.length === 0) return null;

            return (
              <div key={catKey} className="glass-card border border-gray-800 overflow-hidden rounded-xl">
                <div className="bg-gray-900/80 p-4 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{catInfo.emoji}</span>
                    <div>
                      <div className="font-orbitron text-sm font-bold text-white">{catInfo.label}</div>
                      <div className="text-xs text-gray-500">
                        {cat.items_count} abonnement(s) · {cat.monthly_total.toFixed(0)}€/mois
                      </div>
                    </div>
                  </div>
                  {cat.savings_potential_annual > 0 && (
                    <div className="text-right">
                      <div className="text-xs text-gray-500 font-mono">ÉCONOMIES POSSIBLES</div>
                      <div className="text-success font-bold">{cat.savings_potential_annual.toFixed(0)}€/an</div>
                    </div>
                  )}
                </div>
                <div className="divide-y divide-gray-800">
                  {items.map(item => {
                    const isSelected = selectedKeys.has(item.key);
                    const recommendation = item.savings?.recommendation;

                    return (
                      <div
                        key={item.key}
                        className={`p-4 hover:bg-gray-900/30 cursor-pointer transition-colors ${isSelected ? 'bg-cyan-500/5' : ''}`}
                        onClick={() => toggleSelect(item.key)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-cyan-500 border-cyan-500' : 'border-gray-600'}`}>
                            {isSelected && <Check className="w-3 h-3 text-black" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="font-bold text-white truncate">{item.provider_name || item.label_sample}</div>
                              {!item.is_active && (
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700">INACTIF</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 font-mono truncate">{item.label_sample}</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {item.cadence === 'monthly' ? 'Mensuel' : item.cadence === 'quarterly' ? 'Trimestriel' : item.cadence === 'annual' ? 'Annuel' : item.cadence}
                              {' · '}{item.occurrences} occurrence(s)
                            </div>
                            {recommendation && (
                              <div className="mt-2 text-xs bg-success/10 border border-success/30 rounded px-2 py-1 inline-block">
                                <TrendingDown className="w-3 h-3 inline mr-1 text-success" />
                                <span className="text-success">
                                  Passer à <strong>{recommendation.switch_to}</strong> ({recommendation.new_price.toFixed(2)}€/mois) →
                                  économie <strong>{recommendation.savings_annual.toFixed(0)}€/an</strong>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-white">{item.monthly_amount.toFixed(2)}€</div>
                            <div className="text-xs text-gray-500">/mois</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {recurring.length === 0 && (
          <div className="glass-card border border-gray-800 p-12 text-center rounded-xl">
            <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <div className="text-lg font-bold text-white mb-2">Aucun prélèvement récurrent détecté</div>
            <div className="text-sm text-gray-400 mb-6">
              Le fichier a été parsé mais on n'a pas trouvé d'abonnements répétitifs.
              Essaye un relevé sur 3+ mois.
            </div>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={onResetAndReturn} variant="outline">
                Réessayer avec un autre fichier
              </Button>
              <Button onClick={onBackToDashboard} className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold">
                Retour au dashboard
              </Button>
            </div>
          </div>
        )}

        {/* CTA RETOUR DASHBOARD en bas (quand y a des résultats) */}
        {recurring.length > 0 && (
          <div className="mt-12 mb-8 text-center">
            <button
              onClick={onBackToDashboard}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-cyan-400 transition-colors text-sm font-mono"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, color }) => {
  const colorClasses = {
    success: { border: 'border-success/40', label: 'text-success', value: 'text-success' },
    cyan: { border: 'border-cyan-500/40', label: 'text-cyan-400', value: 'text-cyan-400' },
  };
  const c = colorClasses[color] || { border: 'border-gray-800', label: 'text-gray-500', value: 'text-white' };
  return (
    <div className={`glass-card border ${c.border} p-4 rounded-lg`}>
      <div className={`text-xs font-mono uppercase mb-1 ${c.label}`}>{label}</div>
      <div className={`text-2xl font-black ${c.value}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  );
};

export default AuditPackPage;
